import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { UsePaginatedQueryValue } from '@opallabs/api'
import { MultiSelectDiffObject } from '@opallabs/hooks'
import { BoardData, BoardOption, MomentData } from './types'

type BoardsFetchResult = {
  isFetching: boolean
  isFetchedAfterMount: boolean
  data: BoardData[]
}

type UseErrorStateArgs = {
  allowUnscheduling: boolean
  boardsFetchResult: BoardsFetchResult
  momentFetchResult: UsePaginatedQueryValue<MomentData[]>
  initialSelection: BoardOption[]
  selectedBoards: MultiSelectDiffObject<BoardOption> | null
  unschedule: boolean
}

export const useErrorState = ({
  allowUnscheduling,
  boardsFetchResult,
  momentFetchResult,
  initialSelection,
  selectedBoards,
  unschedule,
}: UseErrorStateArgs) => {
  const [hasError, setHasError] = useState(false)
  const showEmptyBoardListError = useRef(false)

  const isLoading =
    (boardsFetchResult.isFetching && !boardsFetchResult.isFetchedAfterMount) ||
    !momentFetchResult.fetchingComplete

  const noDate = useMemo(
    () =>
      unschedule ||
      (allowUnscheduling &&
        momentFetchResult.data &&
        !momentFetchResult.data[0]?.scheduleId),
    [unschedule, allowUnscheduling, momentFetchResult.data]
  )

  const checkError = useCallback(() => {
    const noBoards =
      showEmptyBoardListError.current &&
      !initialSelection.length &&
      !selectedBoards?.added.length
    const boardsRemoved =
      !selectedBoards?.added.length &&
      initialSelection.length > 0 &&
      initialSelection.length === selectedBoards?.removed.length

    return noDate && (boardsRemoved || noBoards)
  }, [
    initialSelection.length,
    selectedBoards?.added.length,
    selectedBoards?.removed.length,
    noDate,
  ])

  // once we interact with the boards list or checkbox,
  // show error if the list is empty, but not on inital mount
  const showEmptyBoardErrorAfterInteraction = () => {
    if (!showEmptyBoardListError.current) {
      showEmptyBoardListError.current = true
    }
  }

  useEffect(() => {
    if (checkError()) {
      setHasError(true)
    } else if (hasError) {
      setHasError(false)
    }
  }, [checkError, hasError])

  return {
    hasError,
    isLoading,
    showEmptyBoardErrorAfterInteraction,
  }
}
